import {
  Checkbox,
  Combobox,
  Group,
  Input,
  type MultiSelectProps,
  Pill,
  PillsInput,
  useCombobox,
} from '@mantine/core'
import {
  transformLabelsToValuesforLanguages,
  transformValuesToLabelforLanguages,
} from '@v2/commons/utils/objectManipulation'
import { useState } from 'react'

interface MultiSelectWithLimitDisplayedProps extends MultiSelectProps {
  data: { label: string; value: string }[]
  maxDisplayedValues?: number
}

const MultiSelectWithLimitDisplayed = ({
  maxDisplayedValues = 2,
  ...props
}: MultiSelectWithLimitDisplayedProps) => {
  const { label, error, withAsterisk, onChange, data, defaultValue } = props
  const combobox = useCombobox({
    onDropdownClose: () => combobox.resetSelectedOption(),
    onDropdownOpen: () => combobox.updateSelectedOptionIndex('active'),
  })

  const [value, setValue] = useState<string[]>(
    transformValuesToLabelforLanguages(defaultValue ?? []),
  )

  const handleValueSelect = (val: string) => {
    setValue((current) => {
      const newValues = current.includes(val)
        ? current.filter((v) => v !== val)
        : [...current, val]
      onChange && onChange(transformLabelsToValuesforLanguages(newValues))
      return newValues
    })
  }

  const handleValueRemove = (val: string) => {
    setValue((current) => {
      const newValues = current.filter((v) => v !== val)
      onChange && onChange(transformLabelsToValuesforLanguages(newValues))
      return newValues
    })
  }

  const values = value
    .slice(
      0,
      maxDisplayedValues === value.length
        ? maxDisplayedValues
        : maxDisplayedValues - 1,
    )
    .map((item) => (
      <Pill
        key={item}
        withRemoveButton
        onRemove={() => handleValueRemove(item)}
      >
        {item}
      </Pill>
    ))

  const options =
    data &&
    data.map((item: { label: string; value: string }, index: number) => {
      return (
        <Combobox.Option
          value={item.label}
          key={index}
          active={value.includes(item.value)}
        >
          <Group gap="sm">
            <Checkbox
              checked={value.includes(item.label)}
              onChange={() => {}}
              aria-hidden
              tabIndex={-1}
              style={{ pointerEvents: 'none' }}
            />
            <span>{item.label}</span>
          </Group>
        </Combobox.Option>
      )
    })

  return (
    <Combobox
      store={combobox}
      onOptionSubmit={handleValueSelect}
      withinPortal={false}
    >
      <Combobox.DropdownTarget>
        <PillsInput
          pointer
          onClick={() => combobox.toggleDropdown()}
          withAsterisk={withAsterisk}
          label={label}
          error={error}
          rightSection={
            <Combobox.Chevron onClick={() => combobox.toggleDropdown()} />
          }
        >
          <Pill.Group>
            {value.length > 0 ? (
              <>
                {values}
                {value.length > maxDisplayedValues && (
                  <Pill>+{value.length - (maxDisplayedValues - 1)} more</Pill>
                )}
              </>
            ) : (
              <Input.Placeholder>Pick one or more values</Input.Placeholder>
            )}
            <Combobox.EventsTarget>
              <PillsInput.Field
                type="hidden"
                onBlur={() => combobox.closeDropdown()}
                onKeyDown={(event) => {
                  if (event.key === 'Backspace') {
                    event.preventDefault()
                    handleValueRemove(value[value.length - 1])
                  }
                }}
              />
            </Combobox.EventsTarget>
          </Pill.Group>
        </PillsInput>
      </Combobox.DropdownTarget>
      <Combobox.Dropdown>
        <Combobox.Options>{options}</Combobox.Options>
      </Combobox.Dropdown>
    </Combobox>
  )
}

export default MultiSelectWithLimitDisplayed
